/**
 * useTransferEventStream
 *
 * Single shared subscriber for the admin transfer SSE stream
 * (`GET /api/transfer/events/stream`). One `EventSource` per browser tab is
 * shared across every consumer (the global bell + the Transfers page) via a
 * module-level singleton — opening a second stream per consumer would double the
 * server's connection count for no benefit.
 *
 * Connection lifecycle mirrors `useAuditStream`: a `hello` on open, named-event
 * listeners, and exponential backoff on a *transient* `error` (a live stream
 * that drops is reconnected with backoff, since the browser's own retry is
 * bypassed once we close the source). A *terminal* error — a non-2xx response
 * such as the 403 returned when inter-instance transfer is disabled — is NOT
 * retried: the browser "fails the connection" (`readyState === CLOSED`) and a
 * reconnect would 403-loop forever. The stream opens only while at least one
 * consumer is subscribed AND the consumer passed `enabled` (admin AND the
 * feature flag; the endpoint is admin- and feature-gated), and closes when the
 * last consumer unsubscribes.
 *
 * Events (mirror the backend contract):
 *  - `transfer.incoming` → an offer summary, broadcast to all admins.
 *  - `notification`      → `{ userId }`, delivered only to that admin's stream.
 */
import { useEffect, useRef } from "react";

// ── Wire shapes (mirror the backend SSE contract) ─────────────────────────────

/** Summary of a freshly-discovered incoming offer (`event: transfer.incoming`). */
export type TransferIncomingEvent = {
  kind: "transfer.incoming";
  offerId: string;
  /** Broker label or opaque instance id — never an address. */
  peer: string;
  manifestHash: string;
  /** Decimal string (BigInt-safe). */
  sizeBytes: string;
  fileCount: number;
  recipientUsername: string | null;
  /** Epoch seconds. */
  createdAt: number;
};

/** Per-admin notification ping (`event: notification`). */
export type TransferNotificationEvent = { userId: number };

export type TransferEventHandlers = {
  onIncoming?: (event: TransferIncomingEvent) => void;
  onNotification?: (event: TransferNotificationEvent) => void;
};

// ── Module-level singleton ────────────────────────────────────────────────────

const MAX_RECONNECT_DELAY_MS = 15_000;
const STREAM_URL = "/api/transfer/events/stream";

const handlerSets = new Set<TransferEventHandlers>();
let source: EventSource | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let retryCount = 0;

const dispatchIncoming = (event: TransferIncomingEvent) => {
  // Snapshot so a handler that unsubscribes mid-dispatch can't mutate the set
  // we're iterating; isolate each handler so one throw can't starve the rest.
  for (const handlers of [...handlerSets]) {
    try {
      handlers.onIncoming?.(event);
    } catch {
      // A consumer handler error must not break the stream.
    }
  }
};

const dispatchNotification = (event: TransferNotificationEvent) => {
  for (const handlers of [...handlerSets]) {
    try {
      handlers.onNotification?.(event);
    } catch {
      // ignore
    }
  }
};

const clearReconnectTimer = () => {
  if (reconnectTimer !== null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
};

const closeStream = () => {
  clearReconnectTimer();
  if (source) {
    source.close();
    source = null;
  }
};

const scheduleReconnect = () => {
  clearReconnectTimer();
  const delay = Math.min(MAX_RECONNECT_DELAY_MS, 1000 * 2 ** retryCount);
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    retryCount += 1;
    openStream();
  }, delay);
};

const openStream = () => {
  if (typeof EventSource === "undefined") return; // SSR / unsupported
  if (source) return; // already open
  if (handlerSets.size === 0) return; // nothing to feed

  const next = new EventSource(STREAM_URL, { withCredentials: true });
  source = next;

  next.addEventListener("hello", () => {
    retryCount = 0;
  });

  next.addEventListener("transfer.incoming", (event) => {
    try {
      const data = JSON.parse((event as MessageEvent).data) as TransferIncomingEvent;
      dispatchIncoming(data);
    } catch {
      // Malformed payload — skip.
    }
  });

  next.addEventListener("notification", (event) => {
    try {
      const data = JSON.parse((event as MessageEvent).data) as TransferNotificationEvent;
      dispatchNotification(data);
    } catch {
      // skip
    }
  });

  next.addEventListener("error", () => {
    // EventSource never exposes the HTTP status, but the spec lets us tell a
    // fatal response from a transient drop by `readyState`, read BEFORE we
    // close (close() forces CLOSED):
    //   • fatal (non-2xx / wrong content-type, e.g. a 403 when the feature is
    //     off) → browser "fails the connection" → CLOSED, no auto-retry.
    //   • transient (a live stream drops) → browser "reestablishes" → CONNECTING.
    // Reconnecting on the fatal case is the 403-loop this guards against.
    const terminal = next.readyState === EventSource.CLOSED;
    if (source) {
      source.close();
      source = null;
    }
    if (terminal) {
      // Give up until a fresh subscribe (feature re-enabled + a new mount).
      clearReconnectTimer();
      retryCount = 0;
      return;
    }
    if (handlerSets.size > 0) scheduleReconnect();
  });
};

/**
 * Subscribe to the shared transfer stream. React-free so the singleton's
 * connect/reconnect behavior is testable without a DOM. When `enabled` is
 * false, nothing opens and the returned cleanup is a no-op. Returns an
 * unsubscribe that closes the stream once the last consumer detaches.
 */
export const subscribeTransferStream = (
  enabled: boolean,
  handlers: TransferEventHandlers,
): (() => void) => {
  if (!enabled) return () => {};
  handlerSets.add(handlers);
  openStream();
  return () => {
    handlerSets.delete(handlers);
    if (handlerSets.size === 0) closeStream();
  };
};

// ── Hook ─────────────────────────────────────────────────────────────────────

type UseTransferEventStreamOptions = TransferEventHandlers & {
  /** Open the stream only when true (admin AND the feature flag — the endpoint
   * is admin- and feature-gated; a disabled install would 403-loop). */
  enabled: boolean;
};

export const useTransferEventStream = ({
  enabled,
  onIncoming,
  onNotification,
}: UseTransferEventStreamOptions): void => {
  // Keep the latest callbacks in a stable handler object so re-renders with
  // inline callbacks don't churn the singleton's subscriber set.
  const handlersRef = useRef<TransferEventHandlers>({});
  handlersRef.current.onIncoming = onIncoming;
  handlersRef.current.onNotification = onNotification;

  useEffect(() => subscribeTransferStream(enabled, handlersRef.current), [enabled]);
};
